import { CheckIcon } from "@heroicons/react/solid";
import { ReactNode, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import whiteLogo from "@/assets/logo/promptlayer-long-light.png";
import noiseBackground from "@/assets/noise-background.png";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ToastType } from "@/enums/toast";
import { useLinkSlackAccount, useSlackLinkInfo } from "@/queries/slack";
import { displayToast } from "@/utils/toast";

const Shell = ({ children }: { children: ReactNode }) => (
  <div className="relative flex h-full min-h-screen flex-col overflow-hidden bg-gradient-to-br from-[#1439A0] to-[#B1C6FF]">
    <img
      src={noiseBackground}
      alt="noise"
      className="absolute left-0 top-0 h-full w-full object-cover opacity-[40%]"
    />
    <div className="absolute z-[51] p-8 phone:p-6">
      <a href="/">
        <img src={whiteLogo} alt="PromptLayer" className="h-8 w-auto" />
      </a>
    </div>
    <main className="z-50 flex w-full flex-1 items-center justify-center px-4">
      <div className="flex h-fit w-full max-w-[424px] flex-col gap-2 rounded-[30px] border border-gray-200 bg-white p-8 shadow-2xl phone:p-4">
        {children}
      </div>
    </main>
  </div>
);

const Message = ({ title, body }: { title: string; body: string }) => (
  <>
    <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
    <p className="text-sm text-gray-600">{body}</p>
  </>
);

const LinkedCard = ({
  title,
  body,
  autoClose = false,
}: {
  title: string;
  body: string;
  autoClose?: boolean;
}) => {
  useEffect(() => {
    if (!autoClose) {
      return undefined;
    }
    const timer = setTimeout(() => window.close(), 1500);
    return () => clearTimeout(timer);
  }, [autoClose]);

  return (
    <>
      <Message title={title} body={body} />
      <Button disabled className="mt-2 w-fit">
        <CheckIcon className="mr-1 h-4 w-4" /> Linked
      </Button>
    </>
  );
};

export const SlackLink = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { data, isLoading, isError } = useSlackLinkInfo(token);
  const linkAccount = useLinkSlackAccount();

  const onLink = () => {
    if (!token) {
      return;
    }
    linkAccount.mutate(token, {
      onError: () =>
        displayToast("Couldn't link your account — the link may have expired.", ToastType.error),
    });
  };

  if (!token) {
    return (
      <Shell>
        <Message
          title="Invalid link"
          body="This Slack link is missing its token. Mention Wrangler in Slack again to get a new one."
        />
      </Shell>
    );
  }

  if (isLoading) {
    return (
      <Shell>
        <Skeleton className="h-7 w-52" />
        <Skeleton className="h-4 w-full" />
      </Shell>
    );
  }

  if (isError || !data) {
    return (
      <Shell>
        <Message
          title="Link expired"
          body="This link is no longer valid. Go back to Slack and mention Wrangler again to get a fresh one."
        />
      </Shell>
    );
  }

  if (linkAccount.isSuccess) {
    return (
      <Shell>
        <LinkedCard
          title="You're linked 🎉"
          body="Head back to Slack and mention Wrangler to start chatting. This tab will close automatically."
          autoClose
        />
      </Shell>
    );
  }

  if (data.alreadyLinked) {
    return (
      <Shell>
        <LinkedCard
          title="Already linked"
          body="Your account is already linked. Head back to Slack and mention Wrangler to chat."
        />
      </Shell>
    );
  }

  if (!data.isMember) {
    return (
      <Shell>
        <Message
          title="Can't link this account"
          body="Your PromptLayer account isn't a member of the organization this Slack workspace is connected to. Switch to the right account or ask the org owner to add you."
        />
      </Shell>
    );
  }

  const teamName = data.slackTeamName ?? "your Slack workspace";
  return (
    <Shell>
      <Message
        title="Link your PromptLayer account"
        body={`Connect your PromptLayer account to ${teamName} so Wrangler can answer as you.`}
      />
      <Button onClick={onLink} isLoading={linkAccount.isLoading} className="mt-2 w-fit">
        Link my account
      </Button>
    </Shell>
  );
};

export default SlackLink;
